import { forwardRef, useMemo, useRef } from 'react'
import type { JSX } from 'react'
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react'

import { clsx } from 'clsx'

import { Flex, Notice, Text, Tooltip } from '@buffer-mono/popcorn'

import { ViewOnlyBadge } from '~publish/components/ViewOnlyBadge'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { MentionHeader, MentionHeader_Mention } from './MentionHeader'
import {
  MentionReactions,
  MentionReactions_Channel,
  MentionReactions_Mention,
} from '../../components/MentionReactions'
import {
  EngagementAvatar,
  EngagementAvatar_Channel,
} from '../../components/EngagementAvatar'
import {
  EngagementItem,
  EngagementItemSkeleton,
} from '../../components/EngagementItem'
import { useCommunityConfig } from '../../CommunityConfig'
import { isViewOnlyChannel } from '../../shared/viewOnlyScenario'
import { useIsAllChannelsView } from '../../util/routeUtils'
import {
  useMentionData,
  useMentionReactionError,
  useMentionReplies,
} from '../context/MentionContext'
import type { MentionData } from '../context/MentionContext'
import { MentionMedia } from '../MentionMedia'
import {
  MentionPostMiniature,
  MentionPostMiniature_Mention,
} from '../MentionPostMiniature'
import { MentionReplyForm, useMentionReplyForm } from '../MentionReplyForm'
import type { UseMentionReplyFormReturn } from '../MentionReplyForm'

import {
  MentionLastReplies,
  MentionLastReplies_Channel,
} from './MentionLastReplies'
import {
  MentionPrefix,
  MentionPrefix_Channel,
  MentionPrefix_Mention,
} from './MentionPrefix'
import { MentionedChannelText_Channel } from '../../components/MentionedChannelText'
import { MentionText } from './MentionText'
import type { MentionedChannel } from '../../util/getMentionedChannelTextParts'
import { mergeSentReplies } from './mergeSentReplies'

import styles from './MentionThreadContent.module.css'

export const MentionThreadContent_Channel = graphql(
  /* GraphQL */ `
    fragment MentionThreadContent_Channel on Channel @_unmask {
      service
      ...EngagementAvatar_Channel
      ...MentionReactions_Channel
      ...MentionPrefix_Channel
      ...MentionLastReplies_Channel
      ...MentionedChannelText_Channel
    }
  `,
  [
    EngagementAvatar_Channel,
    MentionReactions_Channel,
    MentionPrefix_Channel,
    MentionLastReplies_Channel,
    MentionedChannelText_Channel,
  ],
)

/**
 * The reply form still types its `channel` against the full codegen entity,
 * so the channel flowing through the card tree must satisfy both it and the
 * colocated fragment reads. The intersection collapses to the fragment type
 * once `MentionReplyForm` goes fragment-first.
 */
export type MentionThreadContentChannel = FragmentOf<
  typeof MentionThreadContent_Channel
> &
  ComponentProps<typeof MentionReplyForm>['channel']

export const MentionThreadContent_Mention = graphql(
  /* GraphQL */ `
    fragment MentionThreadContent_Mention on Mention @_unmask {
      id
      parentId
      status
      isOwnEngagement
      mentionType
      text
      author {
        username
        avatar
      }
      ...MentionHeader_Mention
      ...MentionReactions_Mention
      ...MentionPrefix_Mention
      ...MentionPostMiniature_Mention
    }
  `,
  [
    MentionHeader_Mention,
    MentionReactions_Mention,
    MentionPrefix_Mention,
    MentionPostMiniature_Mention,
  ],
)

export const MentionContextItem_Mention = graphql(
  /* GraphQL */ `
    fragment MentionContextItem_Mention on Mention @_unmask {
      id
      parentId
      status
      isOwnEngagement
      text
      author {
        username
        avatar
      }
      ...MentionHeader_Mention
    }
  `,
  [MentionHeader_Mention],
)

/**
 * Wrapper for a mention thread row, carrying its semantic grouping.
 * Mirrors `CommentThreadContentContainer`. Shared by the root mention
 * (`MentionThreadContent`) and its read-only ancestors
 * (`MentionContextItem`).
 *
 * The explicit `role="group"` is what keeps a labelled `<section>` from
 * mapping to a `region` landmark: a feed of cards would otherwise add one
 * landmark per row and trip `landmark-unique`. Grouping still reaches
 * screen readers, without polluting the landmark list.
 */
/**
 * Where this mention sits in its thread. `continued` means
 * `MentionsContextSection` renders above it, so the row keeps the top padding
 * that the avatar's connector line bridges. `start` closes that gap.
 */
type MentionThreadPosition = 'start' | 'continued'

type MentionThreadFrameProps = HTMLAttributes<HTMLElement> & {
  mentionId: string
  username: string
  status: MentionData['status']
  isOwnEngagement: boolean
  isContextMention?: boolean
  expanded?: boolean
  threadPosition?: MentionThreadPosition
  children: ReactNode
}

export const MentionThreadFrame = forwardRef<
  HTMLElement,
  MentionThreadFrameProps
>(
  (
    {
      mentionId,
      username,
      status,
      isOwnEngagement,
      className,
      isContextMention,
      expanded,
      threadPosition = 'start',
      children,
      ...props
    },
    ref,
  ): JSX.Element => {
    const semanticLabel = isContextMention
      ? `Context mention from ${username}`
      : `Mention thread from ${username}`

    const statusText =
      status === 'replied'
        ? 'replied to'
        : status === 'dismissed'
          ? 'dismissed'
          : 'needs attention'

    return (
      <section
        ref={ref}
        key={mentionId}
        className={clsx(
          styles.container,
          !isContextMention && threadPosition === 'start' && styles.onlyMention,
          !expanded && styles.collapsed,
          className,
        )}
        data-testid="mention-thread-content"
        data-mention-id={mentionId}
        aria-label={`${semanticLabel} ${
          isOwnEngagement ? '(your reply)' : ''
        } - ${statusText}`}
        // The rule's suggested hosts for `group` (fieldset, details, optgroup)
        // are form/disclosure controls — wrong semantics for a thread row.
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
        role="group"
        data-status={status}
        {...props}
      >
        <div className={styles.mentionList}>{children}</div>
      </section>
    )
  },
)
MentionThreadFrame.displayName = 'MentionThreadFrame'

const MentionThreadContentContainer = forwardRef<
  HTMLElement,
  HTMLAttributes<HTMLElement> & {
    isContextMention?: boolean
    expanded?: boolean
    threadPosition?: MentionThreadPosition
    children: ReactNode
  }
>((props, ref): JSX.Element => {
  const mention = useMentionData()

  return (
    <MentionThreadFrame
      ref={ref}
      mentionId={mention.id}
      username={mention.author?.username ?? 'an account'}
      status={mention.status}
      isOwnEngagement={mention.isOwnEngagement}
      {...props}
    />
  )
})
MentionThreadContentContainer.displayName = 'MentionThreadContentContainer'

/**
 * Body content for the root inbox mention: post miniature, native-reply
 * disclaimer, post caption, or mention text + media — selected by channel
 * capabilities. Where `MainComment` is always `<CommentText /> +
 * <CommentMedia />`, a mention's presentation depends on the channel.
 *
 * @see mentionsCapabilities.ts
 */
const MentionBody = ({
  channel,
  expanded,
}: {
  channel: MentionThreadContentChannel
  expanded: boolean
}): JSX.Element | null => {
  const mention = useMentionData()
  const { config } = useCommunityConfig()

  const isPostKind = mention.mentionType === 'post'
  const hasFullContext = config.mentions.hasFullContext(
    channel,
    mention.mentionType,
  )
  const isNotifyOnly = config.mentions.isNotifyOnly(channel)

  if (isPostKind && hasFullContext) {
    return <MentionPostMiniature channel={channel} />
  }

  if (isNotifyOnly) {
    // Native-reply disclaimer; only when expanded so collapsed cards stay
    // minimal. Post-kind gets the "Commenting" verb (you comment on the
    // source post); comment-kind gets "Replying".
    if (!expanded) return null
    const disclaimer = isPostKind
      ? 'Commenting via Buffer is not available. Open this mention natively to reply.'
      : 'Replying via Buffer is not available. Open this mention natively to reply.'
    return (
      <Text size="sm" color="subtle">
        {disclaimer}
      </Text>
    )
  }

  if (isPostKind) {
    // Partial-context post (e.g. YouTube): no inline miniature, just the
    // post's own caption. The post IS the mention, so the text lives on
    // the mention itself. @see MentionPostMiniature, mentionsCapabilities.ts
    return mention.text ? (
      <MentionText text={mention.text} channel={channel} />
    ) : null
  }

  // Comment-type mention (and fallback): the mention's own body text plus
  // any attached media. Mirrors CommentCard's MainComment, which renders
  // <CommentText /> followed by <CommentMedia />. MentionMedia returns
  // null when there are no assets, so media-only mentions (text === null)
  // still render their attachments.
  return (
    <>
      {mention.text ? (
        <MentionText text={mention.text} channel={channel} />
      ) : null}
      <MentionMedia />
    </>
  )
}

/**
 * MainMention - Renders the root inbox mention's row: avatar, header,
 * prefix, and capability-driven body. Mirrors `MainComment`.
 */
const MainMention = ({
  channel,
  showParentLine,
  showConnector,
  showChannelBadge,
  expanded,
}: {
  channel: MentionThreadContentChannel
  showParentLine: boolean
  showConnector: boolean
  showChannelBadge: boolean
  expanded: boolean
}): JSX.Element => {
  const mention = useMentionData()

  return (
    <EngagementItem
      variant="root"
      aria-labelledby={`mention-header-${mention.id}`}
      data-testid="mention-item"
      data-replied={mention.status === 'replied' ? 'true' : 'false'}
      data-dismissed={mention.status === 'dismissed' ? 'true' : 'false'}
      avatar={
        <EngagementAvatar
          showConnector={showConnector}
          hasParent={showParentLine}
          avatar={mention.author?.avatar ?? ''}
          username={mention.author?.username ?? ''}
          channel={showChannelBadge ? channel : undefined}
        />
      }
    >
      <MentionHeader />
      <MentionPrefix channel={channel} expanded={expanded} />
      <MentionBody channel={channel} expanded={expanded} />
      <MentionReactions channel={channel} />
    </EngagementItem>
  )
}

type MentionThreadContentProps = {
  channel: MentionThreadContentChannel
  expanded?: boolean
  /**
   * Whether an ancestor thread renders above this mention. Drives the upward
   * connector line and the top padding together, so the chrome only points at
   * a thread that is actually on screen. Defaults to `start`.
   */
  threadPosition?: MentionThreadPosition
  /** Fired when a reply publishes — resolves the card (see `MentionCard`). */
  onReplied?: () => void
  /** Collapses this card (wired to outside-click collapse in the reply form). */
  onCollapse?: () => void
  /** Collapses this card and opens the next repliable one after a reply. */
  onExpandNextMention?: (
    fromMentionId?: string,
    onlyRepliable?: boolean,
  ) => void
}

/**
 * Merges locally-sent replies with the server replies from context.
 *
 * @see mergeSentReplies
 */
const useMergedReplies = (
  sentReplies: UseMentionReplyFormReturn['sentReplies'],
  serverReplies: readonly MentionData[],
): MentionData[] => {
  return useMemo(
    () => mergeSentReplies(sentReplies, serverReplies),
    [sentReplies, serverReplies],
  )
}

/**
 * Collapsed-card error banner. When a reply fails and the card is no longer
 * expanded (the form isn't visible to carry its inline error), surface the
 * failure as a notice at the top of the thread. Mirrors Comments'
 * `ReplyErrorMessage`.
 */
const MentionReplyErrorMessage = ({
  errorMessage,
}: {
  errorMessage?: string
}): JSX.Element => {
  return (
    <Notice variant="error" data-error-message="true">
      <Notice.Text>{errorMessage}</Notice.Text>
    </Notice>
  )
}

const VIEW_ONLY_MENTION_TOOLTIP =
  'To reply to and react to mentions in this channel, ask an admin to update your permissions.'

/**
 * The eye + "View only" badge shown at the bottom-right inside an expanded
 * mention card the actor cannot act on. The full explanation lives in the
 * tooltip. Mirrors Comments' `ViewOnlyIndicator`.
 */
const MentionViewOnlyIndicator = (): JSX.Element => (
  <Flex justify="end" fullWidth className={styles.viewOnlyRow}>
    <Tooltip content={VIEW_ONLY_MENTION_TOOLTIP}>
      <ViewOnlyBadge tabIndex={0} />
    </Tooltip>
  </Flex>
)

/**
 * Banner for a failed like/unlike, including the case where the network
 * reports the mention as deleted. Mirrors Comments' `ReactionErrorMessage`.
 */
const MentionReactionErrorMessage = ({
  errorMessage,
}: {
  errorMessage: string
}): JSX.Element => {
  return (
    <Notice variant="error">
      <Notice.Text>{errorMessage}</Notice.Text>
    </Notice>
  )
}

/**
 * The inner content of a `MentionCard` — the root inbox mention's row
 * (avatar, header, prefix, capability body) plus its reply section.
 * Mirrors `CommentThreadContent`. Ancestors shown in
 * `MentionsContextSection` use the lighter `MentionContextItem` instead,
 * since they're read-only thread messages with no reply or capability
 * chrome.
 */
export const MentionThreadContent = forwardRef<
  HTMLElement,
  MentionThreadContentProps
>(
  (
    {
      channel,
      expanded = false,
      threadPosition = 'start',
      onReplied,
      onCollapse,
      onExpandNextMention,
    },
    ref,
  ): JSX.Element => {
    const mention = useMentionData()
    const { config } = useCommunityConfig()
    const { reactionError } = useMentionReactionError()
    const isAllChannelsView = useIsAllChannelsView()
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const reply = useMentionReplyForm({
      channel,
      textareaRef,
      expanded,
      onReplied,
      onCollapse,
      onExpandNextMention,
    })

    // Two independent gates: the service must support replying to this kind of
    // mention, and the actor must hold Community's engagement-management
    // permission. Community uses `manageComments` for both inboxes — there is
    // no `manageMentions`. Mirrors `CommentThreadContent`.
    const canManage = canPerform(channel, 'manageComments')
    const canReply =
      config.mentions.canReply(channel, mention.mentionType) && canManage

    // A replied mention surfaces replies below the main mention — what makes a
    // replied card look distinct from an unreplied one. The list fetches the
    // whole thread tail and provides each card its replies through context;
    // just-sent replies live in form state until the next fetch, so merge both.
    const serverReplies = useMentionReplies()
    const replies = useMergedReplies(reply.sentReplies, serverReplies)

    // Collapsed shows only *your* replies — a quick "did I reply?" glance;
    // expanding widens it to the full thread (everyone's replies) so the
    // conversation context isn't lost. Both come from the same set, split here
    // by `isOwnEngagement`.
    const visibleReplies = expanded
      ? replies
      : replies.filter((replyMention) => replyMention.isOwnEngagement)
    const hasReplies = visibleReplies.length > 0

    // Top connector threads up to the context section when that section is
    // rendered and the card is expanded; bottom connector threads down to
    // whatever sits below the main mention — the replies, the "Sending…" row,
    // or the reply form. `isProcessing` keeps it threaded even after the card
    // auto-collapses mid-send, when `expanded` is already false.
    const isProcessing = reply.status === 'processing'
    const showParentLine = threadPosition === 'continued' && expanded
    const showConnector = hasReplies || (expanded && canReply) || isProcessing

    // In the All Channels view, collapsed cards badge the avatars with the
    // source channel's service icon so you can scan both who mentioned you and
    // which inbox it landed in. The whole thread wears it, main mention and
    // replies alike — they all came from the same channel.
    const showChannelBadge = isAllChannelsView && !expanded

    // When the card is collapsed the form (and its inline error) is gone, so a
    // failed reply needs a standalone banner. Mirrors Comments.
    const showErrorMessage = reply.status === 'error' && !expanded

    // A view-only actor gets the reason the reply form is missing, once the
    // card is expanded — collapsed cards stay clean. Mirrors Comments.
    const showViewOnlyIndicator = expanded && isViewOnlyChannel(channel)

    return (
      <MentionThreadContentContainer
        key={mention.id}
        ref={ref}
        expanded={expanded}
        threadPosition={threadPosition}
      >
        {showErrorMessage && (
          <MentionReplyErrorMessage errorMessage={reply.error} />
        )}
        {reactionError && (
          <MentionReactionErrorMessage errorMessage={reactionError} />
        )}
        <MainMention
          channel={channel}
          showParentLine={showParentLine}
          showConnector={showConnector}
          showChannelBadge={showChannelBadge}
          expanded={expanded}
        />
        <MentionLastReplies
          channel={channel}
          replies={visibleReplies}
          isFormVisible={expanded && canReply}
          isProcessing={isProcessing}
          showChannelBadge={showChannelBadge}
        />
        <MentionReplyForm
          channel={channel}
          reply={reply}
          textareaRef={textareaRef}
          expanded={expanded}
          canReply={canReply}
        />

        {showViewOnlyIndicator && <MentionViewOnlyIndicator />}
      </MentionThreadContentContainer>
    )
  },
)

MentionThreadContent.displayName = 'MentionThreadContent'

/**
 * A read-only context (ancestor) mention rendered inside
 * `MentionsContextSection`. A plain conversation-thread message — avatar,
 * header, text, and media — threaded into the conversation with connector
 * lines.
 *
 * Deliberately none of the root inbox-card chrome: no "Mentioned you in
 * their …" prefix (only the root is definitionally a mention of you, and
 * there's no per-item signal for which ancestors are), no capability
 * variants, and no reply form (you reply on the root mention, not on a
 * specific ancestor). Ancestors are comment-shaped thread items, so a flat
 * text + media body is all they need — mirroring `MainComment`.
 */
export const MentionContextItem = ({
  channel,
}: {
  channel: MentionedChannel
}): JSX.Element => {
  const mention = useMentionData()

  return (
    <MentionThreadContentContainer isContextMention expanded>
      <EngagementItem
        variant="context"
        aria-labelledby={`mention-header-${mention.id}`}
        data-testid="mention-item"
        avatar={
          <EngagementAvatar
            showConnector
            hasParent
            avatar={mention.author?.avatar ?? ''}
            username={mention.author?.username ?? ''}
          />
        }
      >
        <MentionHeader isContextMention />
        {mention.text ? (
          <MentionText text={mention.text} channel={channel} />
        ) : null}
        <MentionMedia />
      </EngagementItem>
    </MentionThreadContentContainer>
  )
}

MentionContextItem.displayName = 'MentionContextItem'

export const MentionThreadContentSkeleton = (): JSX.Element => {
  return (
    <section className={clsx(styles.container)}>
      <div className={styles.mentionList}>
        <EngagementItemSkeleton />
      </div>
    </section>
  )
}
